/**
 * This portal's session.
 *
 * The flow lives in `@glycemia/web-kit`. What belongs here is the one thing
 * that differs from the clinician app, and it is not cosmetic:
 *
 * **A `clientId` IS sent.** The portal's users are invited into a Medplum
 * project (`POST /admin/projects/{id}/invite`), which makes them project-scoped.
 * Medplum looks such a user up only when the login is scoped to that client's
 * project; without it the lookup searches users belonging to NO project, finds
 * nothing, and returns `400 "User not found"`.
 *
 * The clinician app must NOT send one, for the mirror-image reason: its users
 * are global. Same endpoint, same error text, opposite requirement — which is
 * why neither app can rely on a default, and why the flow is shared but the
 * rule is not.
 */

import { createAuth, type Auth, type Session } from '@glycemia/web-kit';

import { getConfig } from './config.js';

export { AuthError } from '@glycemia/web-kit';
export type { Session } from '@glycemia/web-kit';

/**
 * Built on FIRST USE, not at import.
 *
 * ES module imports are evaluated before the importing module's body runs, so
 * `main.tsx` doing `await loadConfig()` before `createRoot().render()` does NOT
 * happen before this file is evaluated — the import chain
 * `main → App → router → auth` runs first. Constructing the client here at
 * module scope therefore read the configuration before it was loaded and threw
 * on a blank page, with the real error only in the console.
 *
 * Deferring removes the ordering requirement rather than documenting it. An
 * invariant that depends on nobody adding an import is not an invariant.
 */
let auth: Auth | null = null;

const client = (): Auth => {
  auth ??= createAuth({
    authBase: import.meta.env['VITE_AUTH_BASE'] ?? '/auth',
    oauthBase: import.meta.env['VITE_OAUTH_BASE'] ?? '/oauth2',
    // A different key from the clinician app's. They are served from different
    // hosts so storage is already separate, but naming it for this surface
    // means a shared-origin deployment could never have one app read the
    // other's token.
    sessionKey: 'glycemia.patient.session',
    clientId: getConfig().medplumClientId,
  });
  return auth;
};

export const signIn = (email: string, password: string): Promise<Session> =>
  client().signIn(email, password);
export const readSession = (): Session | null => client().readSession();
export const signOut = (): void => client().signOut();
export const isExpired = (session: Session, now?: number): boolean =>
  client().isExpired(session, now);
export const refresh = (session: Session): Promise<Session | null> => client().refresh(session);
export const currentAccessToken = (): Promise<string | null> => client().currentAccessToken();
